import React from 'react';

import { Tooltip } from '@/components/Tooltip';
import { StatusChip, StatusChipVariant } from '@/components/legacy-ui/StatusChip';
import {
  EvmScoreCell,
  ScoreCell,
} from '@/sections/customerManagement/components/EvmScoreCells';
import {
  DogPileCustomer,
  DogPileStatus,
  UrgencyTier,
} from '@/sections/customerManagement/types/dogPile.types';
import { getEvmThreshold } from '@/sections/customerManagement/utils/evmThresholds';
import { CUSTOMER_TYPE_LABELS, CustomerTypeValue } from '@/types';
import { cn } from '@/utils/classname';

interface DogPileCardProps {
  customer: DogPileCustomer;
  onStartDogpile: (email: string) => void;
  onViewAccount: (email: string) => void;
}

const urgencyTierStyles: Record<
  UrgencyTier,
  { chipVariant: StatusChipVariant; label: string; className?: string }
> = {
  CRITICAL: { chipVariant: 'critical', label: 'Immediate Attention' },
  HIGH: { chipVariant: 'high', label: 'Needs Attention' },
  MODERATE: { chipVariant: 'neutral', label: 'Monitor Closely' },
  HEALTHY: {
    chipVariant: 'neutral',
    label: 'Delivering Value',
    className: 'dark:bg-green-600! dark:border-green-600!',
  },
};

const dogpileStatusConfig: Record<
  DogPileStatus,
  { label: string; className: string }
> = {
  none: { label: 'No dogpile', className: 'text-chariot-text-secondary' },
  active: { label: 'Dogpile active', className: 'text-chariot-stroke-info' },
  completed: {
    label: 'Dogpile completed',
    className: 'text-chariot-stroke-low',
  },
};

function RiskBadge({
  label,
  count,
  className,
}: {
  label: string;
  count: number;
  className: string;
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[10px] font-semibold tabular-nums',
        className,
        count === 0 && 'opacity-40'
      )}
    >
      {label[0]}: {count}
    </span>
  );
}

function RenewalBadge({ days }: { days: number | undefined }) {
  if (days === undefined || days < 0) return null;

  let label: string;
  let className: string;

  if (days <= 30) {
    label = '< 30 Days';
    className =
      'bg-chariot-stroke-critical/20 text-chariot-stroke-critical border-chariot-stroke-critical/30';
  } else if (days <= 60) {
    label = '< 60 Days';
    className =
      'bg-chariot-stroke-high/20 text-chariot-stroke-high border-chariot-stroke-high/30';
  } else if (days <= 90) {
    label = '< 90 Days';
    className =
      'bg-chariot-stroke-medium/20 text-chariot-stroke-medium border-chariot-stroke-medium/30';
  } else {
    return null;
  }

  return (
    <span
      className={cn(
        'rounded border px-1.5 py-0.5 text-[9px] font-semibold uppercase',
        className
      )}
    >
      Renewal {label}
    </span>
  );
}

export const DogPileCard: React.FC<DogPileCardProps> = ({
  customer,
  onStartDogpile,
  onViewAccount,
}) => {
  const { dotClassName, label: evmLabel } = getEvmThreshold(customer.evm);
  const tierConfig = urgencyTierStyles[customer.urgencyTier];
  const dogpileStatusInfo = dogpileStatusConfig[customer.dogpileStatus];

  const typeLabel =
    customer.type && customer.type in CUSTOMER_TYPE_LABELS
      ? CUSTOMER_TYPE_LABELS[customer.type as CustomerTypeValue]
      : (customer.type ?? 'Unknown');

  const integrationRatio =
    customer.totalIntegrations > 0
      ? Math.round(
          (customer.healthyIntegrations / customer.totalIntegrations) * 100
        )
      : null;

  return (
    <div
      className={cn(
        'bg-chariot-background-secondary border-chariot-stroke rounded-lg border p-4',
        'transition-shadow hover:shadow-xs'
      )}
    >
      {/* Header row */}
      <div className="mb-3 flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          {/* Rank */}
          <span className="text-chariot-text-secondary min-w-[2rem] text-right text-xs font-semibold tabular-nums">
            #{customer.rank}
          </span>

          {/* EVM dot + label */}
          <span className="flex items-center gap-1">
            <span
              className={cn('size-2.5 shrink-0 rounded-full', dotClassName)}
            />
            <span className="text-chariot-text-secondary text-xs">
              {evmLabel}
            </span>
          </span>

          {/* Customer name */}
          <span className="text-chariot-text-primary max-w-[200px] truncate font-semibold">
            {customer.displayName}
          </span>

          {/* Type badge */}
          <span className="text-chariot-text-secondary bg-chariot-background-primary rounded px-1.5 py-0.5 text-[10px] font-medium uppercase">
            {typeLabel}
          </span>
        </div>

        {/* Renewal badge + urgency tier chip */}
        <div className="flex items-center gap-2">
          <RenewalBadge days={customer.timeRemaining?.days} />
          <StatusChip
            label={tierConfig.label}
            variant={tierConfig.chipVariant}
            className={tierConfig.className}
          />
        </div>
      </div>

      {/* Score row */}
      <div className="mb-3 flex items-center gap-4">
        <div className="flex items-center gap-1">
          <Tooltip title="Engagement Value Metric — overall engagement health score (0-10)">
            <span className="text-chariot-text-secondary cursor-help text-xs">
              EVM:
            </span>
          </Tooltip>
          <EvmScoreCell score={customer.evm} />
        </div>
        <div className="flex items-center gap-1">
          <Tooltip title="Delivery Value Score — measures value delivered through risk findings and remediation">
            <span className="text-chariot-text-secondary cursor-help text-xs">
              DVS:
            </span>
          </Tooltip>
          <ScoreCell score={customer.dvs} />
        </div>
        <div className="flex items-center gap-1">
          <Tooltip title="Platform Adoption Score — measures customer's usage of platform features and integrations">
            <span className="text-chariot-text-secondary cursor-help text-xs">
              PAS:
            </span>
          </Tooltip>
          <ScoreCell score={customer.pas} />
        </div>

        {/* Priority score */}
        <div className="ml-auto flex items-center gap-1">
          <span className="text-chariot-text-secondary text-xs">Priority:</span>
          <span className="text-chariot-text-primary text-sm font-bold tabular-nums">
            {customer.priorityScore.toFixed(1)}
          </span>
        </div>
      </div>

      {/* Time remaining row */}
      <div className="mb-3 flex items-center gap-2">
        <span className="text-chariot-text-secondary text-xs">
          Time remaining:
        </span>
        {customer.timeRemaining ? (
          <span
            className={cn(
              'text-xs font-medium',
              customer.timeRemaining.isExpired
                ? 'text-chariot-stroke-critical'
                : customer.urgencyTier === 'CRITICAL'
                  ? 'text-chariot-stroke-critical'
                  : customer.urgencyTier === 'HIGH'
                    ? 'text-chariot-stroke-high'
                    : 'text-chariot-text-primary'
            )}
          >
            {customer.timeRemaining.label}
          </span>
        ) : (
          <span className="text-chariot-text-secondary text-xs">
            No end date
          </span>
        )}
      </div>

      {/* Risk counts row */}
      <div className="mb-3 flex flex-wrap items-center gap-1.5">
        <span className="text-chariot-text-secondary text-xs">Risks:</span>
        <RiskBadge
          label="Critical"
          count={customer.critical}
          className="bg-chariot-stroke-critical text-white"
        />
        <RiskBadge
          label="High"
          count={customer.high}
          className="bg-chariot-stroke-high text-white"
        />
        <RiskBadge
          label="Medium"
          count={customer.medium}
          className="bg-chariot-stroke-medium text-white"
        />
        <RiskBadge
          label="Low"
          count={customer.low}
          className="bg-chariot-stroke-low text-white"
        />
        <RiskBadge
          label="Info"
          count={customer.info}
          className="bg-chariot-stroke-info text-white"
        />
      </div>

      {/* Integration health + dogpile status row */}
      <div className="mb-4 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="text-chariot-text-secondary text-xs">
            Integrations:
          </span>
          {integrationRatio !== null ? (
            <div className="flex items-center gap-1.5">
              <div className="bg-chariot-layer-1 h-1.5 w-20 overflow-hidden rounded-full">
                <div
                  className="bg-chariot-stroke-info h-full rounded-full transition-all duration-300"
                  style={{ width: `${integrationRatio}%` }}
                />
              </div>
              <span className="text-chariot-text-secondary text-xs tabular-nums">
                {customer.healthyIntegrations}/{customer.totalIntegrations}
              </span>
            </div>
          ) : (
            <span className="text-chariot-text-secondary text-xs">—</span>
          )}
        </div>

        {/* Dogpile status */}
        <span className={cn('text-xs', dogpileStatusInfo.className)}>
          {dogpileStatusInfo.label}
        </span>
      </div>

      {/* Action buttons */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => onStartDogpile(customer.email)}
          disabled={customer.dogpileStatus === 'active'}
          className={cn(
            'rounded px-3 py-1.5 text-xs font-medium transition-colors',
            'bg-chariot-brand-primary text-white',
            'hover:bg-chariot-brand-primary/90',
            'disabled:cursor-not-allowed disabled:opacity-40'
          )}
        >
          Start Dogpile
        </button>
        <button
          type="button"
          onClick={() => onViewAccount(customer.email)}
          className={cn(
            'border-chariot-stroke rounded border px-3 py-1.5 text-xs font-medium transition-colors',
            'text-chariot-text-primary hover:bg-chariot-background-primary'
          )}
        >
          View Account
        </button>
      </div>
    </div>
  );
};
